import { yupResolver } from '@hookform/resolvers/yup';
import Close from '@mui/icons-material/Close';
import CloseIcon from '@mui/icons-material/Close';
import {
	Alert,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Stack,
	Typography,
} from '@mui/material';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';
import AppPasswordTextField from '@/common/components/form-inputs/AppPasswordTextField';
import { LoginAccountTarget } from '@/api/DTO/login-account/loginAccount.interface';
import {
	useResetLoginPasswordMutation,
	useSetLoginPasswordMutation,
} from '@/api/login-account/loginAccountApi';

interface Props {
	open: boolean;
	onClose: () => void;
	target: LoginAccountTarget;
	hasHkid: boolean;
	sharedWithKat: boolean;
}

type ResetMethod = 'hkid' | 'manual';

interface FormValues {
	method: ResetMethod;
	password?: string;
}

export default function ResetPasswordDialog(props: Props) {
	const { open, onClose, target, hasHkid, sharedWithKat } = props;
	const { t } = useTranslation();
	const [resetLoginPassword] = useResetLoginPasswordMutation();
	const [setLoginPassword] = useSetLoginPasswordMutation();

	const formSchema = Yup.object().shape({
		method: Yup.string()
			.oneOf(['hkid', 'manual'])
			.required(t('errorMessage.pleaseSelect')),
		password: Yup.string().when('method', {
			is: 'manual',
			then: (schema) =>
				schema.trim().required(t('loginAccount.passwordRequired')),
			otherwise: (schema) => schema.nullable(),
		}),
	});

	const methods = useForm<FormValues>({
		defaultValues: { method: hasHkid ? 'hkid' : 'manual', password: '' },
		resolver: yupResolver(formSchema) as any,
	});
	const method = methods.watch('method');

	useEffect(() => {
		if (open) {
			methods.reset({ method: hasHkid ? 'hkid' : 'manual', password: '' });
		}
	}, [open, hasHkid]);

	const onSubmit: SubmitHandler<FormValues> = async (data) => {
		const response =
			data.method === 'hkid'
				? await resetLoginPassword(target)
				: await setLoginPassword({
						...target,
						password: (data.password ?? '').trim(),
					});
		if ('data' in response) {
			enqueueSnackbar(t('loginAccount.resetPasswordSuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			onClose();
		}
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
			<DialogTitle variant='h5'>{t('loginAccount.resetPassword')}</DialogTitle>
			<IconButton
				aria-label='close'
				onClick={onClose}
				sx={{
					position: 'absolute',
					right: 8,
					top: 8,
					color: (theme) => theme.palette.grey[500],
				}}
			>
				<Close />
			</IconButton>
			<DialogContent>
				<form onSubmit={methods.handleSubmit(onSubmit)}>
					<Stack spacing={2} sx={{ paddingTop: 1 }}>
						{sharedWithKat && (
							<Alert severity='warning'>
								{t('loginAccount.sharedWithKat')}
							</Alert>
						)}
						<AppRadioGroup
							control={methods.control}
							name='method'
							style={{ padding: 0 }}
							options={[
								{ id: 'hkid', name: t('loginAccount.resetToHkid') },
								{ id: 'manual', name: t('loginAccount.enterPassword') },
							]}
							disabledOptions={hasHkid ? [] : ['hkid']}
						/>
						{!hasHkid && (
							<Typography variant='body2' color='text.secondary'>
								{t('loginAccount.hkidEmpty')}
							</Typography>
						)}
						{method === 'manual' && (
							<AppPasswordTextField
								control={methods.control}
								name='password'
								label={t('loginAccount.password') + '*'}
								autoComplete='new-password'
							/>
						)}
					</Stack>
				</form>
			</DialogContent>
			<DialogActions>
				<Button sx={{ textTransform: 'none' }} onClick={onClose}>
					{t('button.cancel')}
				</Button>
				<Button variant='contained' onClick={methods.handleSubmit(onSubmit)}>
					{t('button.confirm')}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
